import { DocumentGroup, caseNotesApi, counselorDocumentsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import {
  fetchCounselorDocumentBlob,
  fetchCounselorDocumentPreviewBlob,
} from "@/common/components/layout/components/dropzone/hooks/dropzoneFetch.utils";
import { useDropZoneUpload } from "@/common/components/layout/components/dropzone/hooks/useDropZoneUpload";
import { useDropzoneSection } from "@/common/components/layout/components/dropzone/hooks/useDropzoneSection";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";

export function useCounselorDropZone(clientId: string | undefined) {
  const { onDrop, uploadingFiles, formatDialog } = useDropZoneUpload();

  const section = useDropzoneSection({
    zone: DocumentGroup.Counselor,
    queryKey: [QueryKey.DropZoneFiles],
    queryFn: async () => {
      return counselorDocumentsApi.getDocumentsFromCounselorDropZone();
    },
    deleteFn: (files) => counselorDocumentsApi.deleteDropZoneFiles({ ids: files.map((f) => f.documentId) }),
    fetchDownloadBlobFn: (file) => fetchCounselorDocumentBlob(file.documentId),
    fetchPreviewBlobFn: (file) => fetchCounselorDocumentPreviewBlob(file.documentId),
    renameFn: (file, newFileName) =>
      counselorDocumentsApi.renameDocumentInCounselorDropZone({
        documentId: file.documentId,
        renameDocumentRequest: { newFileName },
      }),
    createCaseNoteFn: clientId
      ? (file) =>
          caseNotesApi.createCaseNoteFromDocument({
            clientId,
            createCaseNoteFromDocumentRequest: {
              source: DocumentGroup.Counselor,
              fileId: file.documentId,
              originTimeZoneId: getLocalTimeZoneId(),
            },
          })
      : undefined,
    convertToPdfFn: (file) =>
      counselorDocumentsApi.convertDocumentInCounselorDropZoneToPdf(
        { documentId: file.documentId },
        withLocalErrorHandling
      ),
    invalidationKeys: [[QueryKey.DropZoneFiles]],
    caseNoteInvalidationKeys: clientId ? [[QueryKey.ClientCaseNotes, clientId]] : undefined,
    onDrop,
    uploadingFiles,
  });

  return { ...section, formatDialog };
}
